import React, { useState } from 'react';
import { Input, Button, Alert, Space, Typography } from 'antd';
import { KeyOutlined, EyeOutlined, EyeInvisibleOutlined, CheckCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

const ApiKeyInput = ({ 
  label = 'API Key',
  placeholder = 'Enter your API key...',
  value = '',
  onChange,
  onTest = null,
  onSave = null,
  testLabel = 'Test Connection',
  saveLabel = 'Save',
  showTestButton = true,
  showSaveButton = true,
  validator = null,
  helperText = null,
  disabled = false,
  ...props 
}) => {
  const [visible, setVisible] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [validationError, setValidationError] = useState(null);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setTestResult(null);
    setValidationError(null);
    
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleTest = async () => {
    if (!value.trim()) {
      setTestResult({ success: false, message: 'Please enter an API key first' });
      return;
    }

    if (validator) {
      const validation = validator(value);
      if (!validation.valid) {
        setValidationError(validation.message);
        return;
      }
    }

    setTesting(true);
    setTestResult(null);
    setValidationError(null);

    try {
      const result = await onTest(value);
      setTestResult(result);
    } catch (error) {
      setTestResult({ 
        success: false, 
        message: error.message || 'Test failed' 
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!value.trim()) {
      setValidationError('Please enter an API key');
      return;
    }

    if (validator) {
      const validation = validator(value);
      if (!validation.valid) {
        setValidationError(validation.message);
        return;
      }
    }

    setSaving(true);
    setValidationError(null);

    try {
      await onSave(value);
    } catch (error) {
      setValidationError(error.message || 'Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  const toggleVisibility = () => {
    setVisible(!visible);
  };

  return (
    <div className="api-key-input">
      <div style={{ marginBottom: 8 }}>
        <Text strong>{label}</Text>
      </div>
      
      <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
        <Input
          prefix={<KeyOutlined />}
          type={visible ? 'text' : 'password'}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          status={validationError || (testResult && !testResult.success) ? 'error' : ''}
          {...props}
        />
        <Button 
          icon={visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          onClick={toggleVisibility}
          disabled={disabled}
        />
      </Space.Compact>

      <Space wrap style={{ marginBottom: 16 }}>
        {showTestButton && onTest && (
          <Button
            onClick={handleTest}
            loading={testing}
            disabled={disabled || !value.trim()}
            type="default"
          >
            {testLabel}
          </Button>
        )}
        
        {showSaveButton && onSave && (
          <Button
            onClick={handleSave}
            loading={saving}
            disabled={disabled || !value.trim()}
            type="primary"
          >
            {saveLabel}
          </Button>
        )}
      </Space>

      {helperText && (
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {helperText}
          </Text>
        </div>
      )}

      {validationError && (
        <Alert
          message={validationError}
          type="error"
          showIcon
          style={{ marginBottom: 8 }}
        />
      )}

      {testResult && (
        <Alert
          message={testResult.success ? 'Connection Successful' : 'Connection Failed'}
          description={testResult.message}
          type={testResult.success ? 'success' : 'error'}
          showIcon
          icon={testResult.success ? <CheckCircleOutlined /> : undefined}
          style={{ marginBottom: 8 }}
        />
      )}
    </div>
  );
};

export default ApiKeyInput;
